import { getCMSClientId, getCMSJWT, makeAuthenticatedRequest } from './auth.service';
import { CMS_BASE_URL } from '../config/api.config';

/** The last read of the client's packs, for the record it was read for (peekMyPackages). */
let lastWallet: { key: string; packs: ClientPackage[] } | null = null;
const walletKey = (): string | null => {
  const jwt = getCMSJWT();
  return jwt ? `${getCMSClientId() ?? ''}:${jwt}` : null;
};

/**
 * A prepaid package as its owner sees it. Mirrors StorefrontPackDto on the
 * API — deliberately narrower than the admin view, which carries per-credit
 * bookkeeping a client has no use for.
 */
/** One eligibility rule of a pack. Null option = any size of the service. */
export interface PackLine {
  groomingServiceId: number;
  groomingServicePricingOptionId?: number | null;
  serviceName: string;
  optionName?: string | null;
  /** List price of this option — struck through when a session pays. */
  price?: number;
  /** The session only applies when this service is on the visit. */
  isRequired?: boolean;
}

/** One session of a pack, as its owner sees it. */
export interface PackSession {
  creditId: number;
  /** used | booked | available | expired | forfeited | refunded */
  status: string;
  serviceName?: string | null;
  petName?: string | null;
  staffName?: string | null;
  whenUtc?: string | null;
  appointmentId?: number | null;
}

export interface ClientPackage {
  id: number;
  /** The template it was minted from — how the catalogue knows it's owned. */
  bundleTemplateId?: number;
  name: string;
  description?: string | null;
  sessionsTotal: number;
  /** Available + booked — sessions they can still use. */
  sessionsLeft: number;
  /** Held by an appointment that hasn't happened yet. */
  sessionsBooked: number;
  expiresAtUtc: string;
  purchasedAtUtc: string;
  isExpired: boolean;
  /** Every session spent — nothing left to book. */
  isUsedUp: boolean;
  balanceOutstanding: number;
  /** An unpaid balance is blocking use. */
  isLockedUntilPaid: boolean;
  eligibleServices: string[];
  /** Structured eligibility — what the booking flow matches against. */
  eligibleLines: PackLine[];
  /** Credits free to spend; a booking line's serviceCreditId comes from here. */
  availableCreditIds: number[];
  /** Every session with what it went on — history + appointment join. */
  sessions: PackSession[];
  /** Catalog placement, so the pack can sit among its category's services. */
  groomingServiceCategoryId?: number | null;
  categoryName?: string | null;
  categoryColorHexCode?: string | null;
  /** Cat=1, Dog=2 — the species this pack was sold for. */
  categoryGroomingServiceType?: number | null;
  sortOrder: number;
  petName?: string | null;
  termsAndConditions?: string | null;
  /** Photos, cover first — a bundle is catalogued like a service. */
  imageUrls?: string[];
}

/** How a pack's price is computed. Mirrors the API's BundlePriceMode. */
export const PACK_PRICE_MODE = { flat: 1, percentOff: 2, amountOff: 3 } as const;

/** A pack the client does NOT own yet — the shop's price list. */
export interface SellablePack {
  bundleTemplateId: number;
  name: string;
  description?: string | null;
  sessionsCount: number;
  /**
   * What the client pays — for a dynamic pack this is the FROM figure at
   * the cheapest sizes; the real price is their picks run through
   * priceMode/priceValue (see sellablePackPrice).
   */
  price: number;
  /** Worth at list price — one pick per step for a stepped pack. */
  referencePrice: number;
  /** 1 flat · 2 % off · 3 $ off — how the price is computed. */
  priceMode?: number;
  priceValue?: number;
  /** True when the price follows the picked sizes (not flat). */
  isDynamicPrice?: boolean;
  validityMonths: number;
  groomingServiceCategoryId?: number | null;
  categoryName?: string | null;
  categoryColorHexCode?: string | null;
  categoryGroomingServiceType?: number | null;
  sortOrder: number;
  imageUrls: string[];
  termsAndConditions?: string | null;
  eligibleLines: PackLine[];
}

/**
 * One cart line, as far as money is concerned.
 *
 * Deliberately structural rather than tied to any one component's type:
 * the footer, the cart sheet and the review page all hold the same lines
 * in different shapes, and they must not each invent their own arithmetic.
 */
export interface CartMoneyLine {
  price?: number;
  quantity?: number;
  usePackSession?: boolean;
  /** A pack already owned — the session was paid for long ago. */
  packId?: number;
  /** A pack being bought with this booking. */
  packTemplateId?: number;
  packPrice?: number;
  /** Whatever distinguishes one pet from another in the caller's shape. */
  petKey: string | number;
}

/**
 * What a cart is worth, once and for everyone.
 *
 * Three rules, in one place because they were previously in three:
 *   · a line a session covers costs nothing — the money was taken when the
 *     pack was bought, so it is never added and never subtracted;
 *   · a pack being bought joins the bill at its own price, once per
 *     (pack, pet) however many services ride its session;
 *   · sessions count in distinct (pack, pet) pairs — one session covers a
 *     pet's whole visit.
 *
 * The footer summed every line's price with no notion of packs at all,
 * so a $200 package covering $233 of services read "$233" beside a cart
 * that said $200.
 */
export const cartMoney = (lines: CartMoneyLine[]) => {
  const covered = (l: CartMoneyLine) =>
    !!l.usePackSession && (l.packId != null || l.packTemplateId != null);

  const services = lines.reduce(
    (n, l) => n + (covered(l) ? 0 : (l.price ?? 0) * (l.quantity ?? 1)),
    0
  );

  const sessions = new Set(
    lines
      .filter(covered)
      .map((l) => `${l.packId ?? `t${l.packTemplateId}`}:${l.petKey}`)
  ).size;

  const charged = new Set<string>();
  const packs = lines.reduce((n, l) => {
    if (l.packTemplateId == null) return n;
    const key = `${l.packTemplateId}:${l.petKey}`;
    if (charged.has(key)) return n;
    charged.add(key);
    return n + (l.packPrice ?? 0);
  }, 0);

  return { services, packs, sessions, total: services + packs };
};

/** One picked size, as far as pack pricing is concerned. */
export interface PackPricePick {
  serviceId: number;
  price: number;
}

/**
 * What a pack for sale actually costs with these picks — the client mirror
 * of the server's pick-priced mint, kept beside cartMoney for the same
 * reason: one arithmetic, everywhere.
 *
 * A flat pack costs its authored price whatever is picked. A dynamic
 * (%-off / $-off) pack is worth sessions × the picked services' prices —
 * required services all price in, a permissive pack prices (and narrows
 * to) exactly what's picked. Anything short of a full pick set falls back
 * to the catalogue's from-price; the server re-derives the real figure at
 * purchase either way.
 */
export const sellablePackPrice = (
  pack: SellablePack,
  picks: PackPricePick[]
): number => {
  if (!pack.isDynamicPrice || pack.priceMode == null || pack.priceValue == null) {
    return pack.price;
  }
  const requiredIds = Array.from(
    new Set(
      pack.eligibleLines.filter((l) => l.isRequired).map((l) => l.groomingServiceId)
    )
  );
  const pricedIds =
    requiredIds.length > 0
      ? requiredIds
      : Array.from(new Set(picks.map((p) => p.serviceId)));
  if (pricedIds.length === 0) return pack.price;

  let perSession = 0;
  for (const sid of pricedIds) {
    const pick = picks.find((p) => p.serviceId === sid);
    if (!pick) return pack.price;
    perSession += pick.price;
  }

  const reference = Math.max(1, pack.sessionsCount) * perSession;
  const price =
    pack.priceMode === PACK_PRICE_MODE.percentOff
      ? reference * (1 - pack.priceValue / 100)
      : reference - pack.priceValue;
  return Math.max(0, Math.round(price * 100) / 100);
};

/**
 * The lines a pack's session pays for. A bundle's session pays its REQUIRED
 * services only: an optional service in a bundle is an extra at its own price,
 * never on the session (Lena, 14 Oct 2026; mpgadmin G123, storefront G31). A
 * permissive pack (nothing required) pays any of its lines, as always.
 */
export const sessionLines = (lines: PackLine[] | null | undefined): PackLine[] => {
  const all = lines ?? [];
  const required = all.filter((l) => l.isRequired);
  return required.length > 0 ? required : all;
};

/** A pack the client can actually book against right now. */
export const isPackUsable = (pack: ClientPackage): boolean =>
  !pack.isExpired && !pack.isUsedUp && !pack.isLockedUntilPaid;

/**
 * Sessions still free to spend. `sessionsLeft` counts those already held by
 * an upcoming booking too, because they're still the client's — but "you can
 * book N more" has to exclude them or the number promises twice.
 */
export const freeSessions = (pack: ClientPackage): number =>
  Math.max(0, pack.sessionsLeft - pack.sessionsBooked);

export const packageService = {
  /**
   * The signed-in client's packages. The proxy reads the client id from
   * their token, so there is nothing to pass and nothing to spoof.
   */
  async fetchMyPackages(): Promise<ClientPackage[]> {
    const key = walletKey();
    const response = await makeAuthenticatedRequest(
      `${CMS_BASE_URL}/api/client-package/by-client`,
      { method: 'GET' }
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch packages: ${response.status}`);
    }

    const data = await response.json();
    const packs = Array.isArray(data) ? data : [];
    if (key && key === walletKey()) lastWallet = { key, packs };
    return packs;
  },

  /**
   * The signed-in record's packs as last read this visit, or undefined. The
   * services step opens on them (their cards sit above its list) and reads
   * them again behind; every other page reads afresh. Memory only, dropped
   * at sign-out (G1).
   */
  peekMyPackages(): ClientPackage[] | undefined {
    const key = walletKey();
    return key && lastWallet?.key === key ? lastWallet.packs : undefined;
  },

  /** Sign-out (G1). */
  forgetMyPackages(): void {
    lastWallet = null;
  },

  /**
   * The packs this store sells online — the shop's price list, not the
   * client's wallet. Public by design: its whole job is showing packs to
   * someone who owns none and may not be signed in, so it uses a plain
   * fetch rather than the authenticated wrapper.
   */
  async fetchStorePacks(storeId: number): Promise<SellablePack[]> {
    try {
      const response = await fetch(
        `${CMS_BASE_URL}/api/pack-catalog/by-store?storeId=${storeId}`,
        { method: 'GET', headers: { 'Content-Type': 'application/json' } }
      );
      if (!response.ok) return [];
      const data = await response.json();
      return Array.isArray(data) ? data : [];
    } catch {
      // A shop with no catalogue is a quieter failure than a broken page.
      return [];
    }
  },

  /**
   * Whether to show the "you have packages" affordance at all. Cheap enough
   * to call on menu open; returns 0 rather than throwing for signed-out
   * visitors, who simply have none.
   */
  async countUsablePackages(): Promise<number> {
    try {
      const packs = await packageService.fetchMyPackages();
      return packs.filter(isPackUsable).length;
    } catch {
      return 0;
    }
  }
};

/** How a pack relates to one (service, option) pair. */
export type PackCoverage =
  | { kind: 'exact' | 'eligible'; pack: ClientPackage }
  | { kind: 'none' };

/**
 * The wallet maths shared by the balance strip, the service cards, the
 * cart and the review step. Pure functions over the fetched packs plus
 * the booking's own pending spends, so every frame reads one truth.
 *
 * The product rule: ONE session covers a pet's whole visit with a pack —
 * a second eligible service for the same pet rides on the same session.
 * Pending spend is therefore counted in distinct (pack, pet) pairs, not
 * in covered lines.
 */
export const packageWallet = {
  usablePacks(packs: ClientPackage[]): ClientPackage[] {
    return packs
      .filter(isPackUsable)
      .filter((p) => (p.availableCreditIds ?? []).length > 0);
  },

  /**
   * Coverage of a specific pricing option. 'exact' — a line names this
   * very option, or covers any size of the service: adding it can apply
   * a session without asking anything else. 'eligible' — the pack covers
   * the service but is pinned to a DIFFERENT option, so it only offers
   * context ("only the XS option matches").
   */
  /**
   * A pack that requires services is a BUNDLE: the session buys the whole
   * combination, so it must never be offered against a lone service card.
   * Its optional services are extras at their own price, added from the
   * bundle's sheet as ordinary lines: the session never pays for them, and
   * they say nothing about booking that service on its own (Lena, 14 Oct
   * 2026; mpgadmin G123, storefront G31; `sessionLines`).
   */
  /**
   * A pack with required services is a BUNDLE: everything about it —
   * coverage, tags, redemption — lives on the pack's own card and sheet,
   * NEVER on the individual service cards. Mixing the two surfaces has
   * been explicitly rejected; keep them apart.
   */
  isBundle(pack: ClientPackage): boolean {
    return (pack.eligibleLines ?? []).some((l) => l.isRequired);
  },

  /** Packs that can pay for a single service picked off the list. */
  perServicePacks(packs: ClientPackage[]): ClientPackage[] {
    return this.usablePacks(packs).filter((p) => !this.isBundle(p));
  },

  coverageForOption(
    packs: ClientPackage[],
    serviceId: number,
    optionId: number
  ): PackCoverage {
    for (const pack of this.perServicePacks(packs)) {
      for (const line of pack.eligibleLines ?? []) {
        if (line.groomingServiceId !== serviceId) continue;
        if (
          line.groomingServicePricingOptionId == null ||
          line.groomingServicePricingOptionId === optionId
        ) {
          return { kind: 'exact', pack };
        }
      }
    }
    // "Covers another option only": only what a session pays for. A bundle's
    // optional service is an extra at its own price (G31), so it never says
    // a pack covers it.
    for (const pack of this.usablePacks(packs)) {
      if (sessionLines(pack.eligibleLines).some((l) => l.groomingServiceId === serviceId)) {
        return { kind: 'eligible', pack };
      }
    }
    return { kind: 'none' };
  },

  /** Whether any usable pack covers the service at all (card-level hint). */
  coverageForService(packs: ClientPackage[], serviceId: number): PackCoverage {
    for (const pack of this.perServicePacks(packs)) {
      if ((pack.eligibleLines ?? []).some((l) => l.groomingServiceId === serviceId)) {
        return { kind: 'eligible', pack };
      }
    }
    return { kind: 'none' };
  },

  /**
   * Sessions this booking will spend: one per distinct (pack, pet) among
   * the covered selections.
   */
  pendingSessions(covered: Array<{ packId: number; petKey: string }>): number {
    return new Set(covered.map((c) => `${c.packId}:${c.petKey}`)).size;
  },

  /** Sessions THIS booking holds from one pack (distinct pets, per the rule). */
  pendingForPack(
    covered: Array<{ packId: number; petKey: string }>,
    packId: number
  ): number {
    return new Set(
      covered.filter((c) => c.packId === packId).map((c) => c.petKey)
    ).size;
  },

  /**
   * One pack's meter — SPENDABLE sessions only: free, held by this booking,
   * held by a future appointment. Spent sessions are deliberately absent.
   *
   * The aggregate meter this replaces drew a pip for every session in every
   * non-expired pack, so a pack you'd used up still padded the row with
   * greys while the caption counted only usable packs. "6 available" sat
   * above sixteen pips and read as ten lost sessions. A pack's full history
   * belongs on its own card in My packages, not in the booking flow.
   */
  packPips(
    pack: ClientPackage,
    covered: Array<{ packId: number; petKey: string }>
  ): Array<'free' | 'pending' | 'booked' | 'spent'> {
    const freeAll = (pack.availableCreditIds ?? []).length;
    const pending = Math.min(this.pendingForPack(covered, pack.id), freeAll);
    const free = freeAll - pending;
    const booked = Math.max(0, pack.sessionsBooked);
    // Spent sessions ARE drawn, greyed. Dropping them made the meter as
    // long as whatever was left, so a 4-session pack with one used showed
    // three pips and read as if a session had gone missing. Scoped to one
    // named pack, a grey pip is legible: "1 of 4 used".
    const spent = Math.max(0, pack.sessionsTotal - free - pending - booked);
    return [
      ...Array<'free'>(free).fill('free'),
      ...Array<'pending'>(pending).fill('pending'),
      ...Array<'booked'>(booked).fill('booked'),
      ...Array<'spent'>(spent).fill('spent')
    ];
  },

  /**
   * The pack's sessions in words, so the meter never has to be decoded.
   * Only non-zero parts appear.
   */
  packBreakdown(
    pack: ClientPackage,
    covered: Array<{ packId: number; petKey: string }>
  ): string {
    const pips = this.packPips(pack, covered);
    const count = (k: string) => pips.filter((p) => p === k).length;
    const parts: string[] = [];
    if (count('free') > 0) parts.push(`${count('free')} available`);
    if (count('pending') > 0) parts.push(`${count('pending')} in this booking`);
    if (count('booked') > 0) parts.push(`${count('booked')} booked`);
    if (count('spent') > 0) parts.push(`${count('spent')} used`);
    return parts.join(' · ');
  },

  /**
   * Packs that may be spent on THIS pet. A pack belongs to a species — a
   * dog pack must not be offered while a cat is being booked for, and its
   * Covered pills must not appear on cat services.
   *
   * A pack with no species (older data, or no category yet) is left
   * available rather than hidden: withholding a pack someone paid for is
   * the worse failure.
   */
  packsForSpecies(packs: ClientPackage[], petType?: 'dog' | 'cat'): ClientPackage[] {
    return this.forSpecies(packs, petType);
  },

  /**
   * The species rule, for anything filed under a category — a pack the
   * client owns or one the shop is selling. Both are catalogued the same
   * way, so both are filtered the same way.
   */
  forSpecies<T extends { categoryGroomingServiceType?: number | null }>(
    items: T[],
    petType?: 'dog' | 'cat'
  ): T[] {
    if (!petType) return items;
    const wanted = petType === 'cat' ? 1 : 2;
    return items.filter(
      (p) => p.categoryGroomingServiceType == null || p.categoryGroomingServiceType === wanted
    );
  },

  /**
   * The shop's catalogue for this pet's species.
   *
   * A template the client already owns STAYS on the shelf: owning a pack
   * doesn't stop them buying another, at whatever size they like — the
   * new pack pins to its own picks while the old one keeps redeeming at
   * the size it was bought. The owned pack renders as its own card with
   * its sessions; the caller labels the for-sale card ("buy another") so
   * the two never read as duplicates.
   */
  sellableFor(storePacks: SellablePack[], petType?: 'dog' | 'cat'): SellablePack[] {
    return this.forSpecies(storePacks, petType);
  },

  /** A sellable pack whose session buys a fixed combination of services. */
  isSellableBundle(pack: SellablePack): boolean {
    return (pack.eligibleLines ?? []).some((l) => l.isRequired);
  },

  /** Distinct service names a sellable pack covers, for its card. */
  sellableServiceNames(pack: SellablePack): string[] {
    const lines = pack.eligibleLines ?? [];
    const required = lines.filter((l) => l.isRequired);
    return Array.from(
      new Set((required.length > 0 ? required : lines).map((l) => l.serviceName))
    );
  },

  /**
   * The pack's REQUIRED services, each with the sizes on offer. One
   * session covers all of them, so the builder asks for one size per
   * service — titled with the service's own name. Empty means a
   * permissive pack: any covered service, nothing compulsory.
   */
  requiredServices(pack: ClientPackage): Array<{ serviceId: number; serviceName: string; options: PackLine[] }> {
    const required = (pack.eligibleLines ?? []).filter((l) => l.isRequired);
    const byService = new Map<number, { serviceId: number; serviceName: string; options: PackLine[] }>();
    required.forEach((l) => {
      const entry = byService.get(l.groomingServiceId) ?? {
        serviceId: l.groomingServiceId,
        serviceName: l.serviceName,
        options: []
      };
      entry.options.push(l);
      byService.set(l.groomingServiceId, entry);
    });
    return Array.from(byService.values());
  },

  /**
   * The catalogue view of the wallet: one card per PRODUCT, not per receipt.
   *
   * Buying the same bundle twice mints two packs, and listing both put the
   * same bundle on the shelf twice with nothing to tell them apart — the
   * client picks a bundle, not which of their purchases to draw from. So
   * identical packs (same name, same eligibility) collapse into one entry
   * whose sessions are the group's put together, spending the one already
   * committed to this booking first so a second tap tops up the same pack
   * rather than opening a fresh one.
   */
  catalogue(
    packs: ClientPackage[],
    covered: Array<{ packId: number; petKey: string }>,
    petKey?: string
  ): Array<{ pack: ClientPackage; free: number; held: number }> {
    const groups = new Map<string, ClientPackage[]>();
    for (const p of this.usablePacks(packs)) {
      const shape = (p.eligibleLines ?? [])
        .map((l) => `${l.groomingServiceId}/${l.groomingServicePricingOptionId ?? '*'}/${l.isRequired ? 'R' : 'O'}`)
        .sort()
        .join('|');
      const key = `${p.name}#${p.groomingServiceCategoryId ?? ''}#${shape}`;
      groups.set(key, [...(groups.get(key) ?? []), p]);
    }
    return Array.from(groups.values()).map((group) => {
      const held = group.reduce((n, p) => n + this.pendingForPack(covered, p.id), 0);
      const free = group.reduce((n, p) => n + (p.availableCreditIds ?? []).length, 0) - held;
      // This pet's session, if one is already committed, has to be the one
      // we go back to: a second covered service rides on it, and pointing
      // at a sibling pack would silently spend a second session.
      const mine =
        petKey != null
          ? group.find((p) =>
              covered.some((c) => c.packId === p.id && c.petKey === petKey)
            )
          : undefined;
      const spendable = group.find(
        (p) => (p.availableCreditIds ?? []).length - this.pendingForPack(covered, p.id) > 0
      );
      return { pack: mine ?? spendable ?? group[0], free, held };
    });
  },

  /** Distinct service names a pack redeems against, for its subtitle. */
  coveredServiceNames(pack: ClientPackage): string[] {
    return Array.from(
      new Set(sessionLines(pack.eligibleLines).map((l) => l.serviceName))
    );
  },

  /** Free sessions across the wallet, minus what this booking holds. */
  freeAfterPending(
    packs: ClientPackage[],
    covered: Array<{ packId: number; petKey: string }>
  ): number {
    const free = this.usablePacks(packs).reduce(
      (n, p) => n + p.availableCreditIds.length,
      0
    );
    return Math.max(0, free - this.pendingSessions(covered));
  },

  /**
   * The aggregate pip meter: free → held by THIS booking → booked
   * elsewhere → spent, in that order, across every pack the client owns.
   */
  aggregatePips(
    packs: ClientPackage[],
    covered: Array<{ packId: number; petKey: string }>
  ): Array<'free' | 'pending' | 'booked' | 'spent'> {
    const usable = packs.filter((p) => !p.isExpired);
    const total = usable.reduce((n, p) => n + p.sessionsTotal, 0);
    const booked = usable.reduce((n, p) => n + p.sessionsBooked, 0);
    const freeAll = this.usablePacks(packs).reduce(
      (n, p) => n + p.availableCreditIds.length,
      0
    );
    const pending = Math.min(this.pendingSessions(covered), freeAll);
    const free = freeAll - pending;
    const pips: Array<'free' | 'pending' | 'booked' | 'spent'> = [];
    for (let i = 0; i < total; i++) {
      if (i < free) pips.push('free');
      else if (i < free + pending) pips.push('pending');
      else if (i < free + pending + booked) pips.push('booked');
      else pips.push('spent');
    }
    return pips;
  },

  /**
   * Turns covered selections into concrete credit ids for the payload:
   * one credit per (pack, pet), shared by every covered line of that pet.
   * Returns null for a selection whose pack has run dry by assignment
   * time — the caller books that line at retail rather than failing.
   */
  assignCredits(
    packs: ClientPackage[],
    covered: Array<{ packId: number; petKey: string }>
  ): Map<string, number | null> {
    const byPack = new Map<number, number[]>();
    for (const p of packs) byPack.set(p.id, [...(p.availableCreditIds ?? [])]);
    const out = new Map<string, number | null>();
    for (const c of covered) {
      const key = `${c.packId}:${c.petKey}`;
      if (out.has(key)) continue;
      const pool = byPack.get(c.packId) ?? [];
      out.set(key, pool.length > 0 ? (pool.shift() as number) : null);
    }
    return out;
  },
};

/**
 * The pack (and which session of it) holding a booking line's credit —
 * how the appointment page tells "this booking holds session 3 of
 * Wash & Dry ×6". Session numbers follow the pack's story order.
 */
export const findSessionForCredit = (
  packs: ClientPackage[],
  creditId: number
): { pack: ClientPackage; session: PackSession; sessionNumber: number } | null => {
  for (const pack of packs) {
    const idx = (pack.sessions ?? []).findIndex((sn) => sn.creditId === creditId);
    if (idx >= 0) {
      return { pack, session: pack.sessions[idx], sessionNumber: idx + 1 };
    }
  }
  return null;
};
